<template>
  <div class="container">
    <svg
      viewBox="-200 -150 400 400"
      xmlns="http://www.w3.org/2000/svg"
      width="600"
      height="600"
      id="dog-svg"
    >
      <defs>
        <clipPath id="leftEyeClipPath">
          <polyline :points="eyeLeftContour" />
        </clipPath>
        <clipPath id="rightEyeClipPath">
          <polyline :points="eyeRightContour" />
        </clipPath>

        <filter id="fuzzy">
          <feTurbulence
            id="turbulence"
            baseFrequency="0.05"
            numOctaves="3"
            type="noise"
            result="noise"
          />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
        </filter>
      </defs>

      <title>That's an ugly dog</title>
      <desc>CREATED BY UGLY-DOG GENERATOR</desc>

      <!-- Background -->
      <rect
        x="-200"
        y="-150"
        width="400"
        height="400"
        :fill="backgroundColor"
      />

      <!-- Tail (draw first, behind everything) -->
      <polyline
        v-if="tail.length > 0"
        :points="tail"
        :fill="furColor"
        stroke="black"
        :stroke-width="6"
        stroke-linejoin="round"
        stroke-linecap="round"
        filter="url(#fuzzy)"
      />

      <!-- Body -->
      <polygon
        v-if="bodyPoints.length > 0"
        :points="bodyPoints"
        :fill="furColor"
        stroke="black"
        :stroke-width="3.0"
        stroke-linejoin="round"
        filter="url(#fuzzy)"
      />

      <!-- Legs -->
      <polygon
        v-for="(leg, index) in legs"
        :key="'leg-' + index"
        :points="leg"
        :fill="furColor"
        stroke="black"
        :stroke-width="2.5"
        stroke-linejoin="round"
        filter="url(#fuzzy)"
      />

      <!-- Ears (behind head) -->
      <g id="ears">
        <polyline
          id="leftEar"
          :points="leftEar"
          :fill="furColor"
          stroke="black"
          :stroke-width="2.5"
          stroke-linejoin="round"
          filter="url(#fuzzy)"
        />
        <polyline
          id="rightEar"
          :points="rightEar"
          :fill="furColor"
          stroke="black"
          :stroke-width="2.5"
          stroke-linejoin="round"
          filter="url(#fuzzy)"
        />
      </g>

      <!-- Head -->
      <polyline
        id="headContour"
        :points="headPoints"
        :fill="furColor"
        stroke="black"
        :stroke-width="3.0"
        stroke-linejoin="round"
        filter="url(#fuzzy)"
      />

      <!-- Fur patches/spots -->
      <g id="furPatches" v-if="showPatches">
        <polyline
          v-for="(patch, index) in patches"
          :key="'patch-' + index"
          :points="patch"
          :fill="patchColor"
          stroke="black"
          :stroke-width="1.5"
          stroke-linejoin="round"
          filter="url(#fuzzy)"
        />
      </g>

      <g id="spots" v-if="showSpots">
        <circle
          v-for="(spot, index) in spots"
          :key="'spot-' + index"
          :cx="spot.x"
          :cy="spot.y"
          :r="spot.radius"
          :fill="spotColor"
          stroke="black"
          :stroke-width="1.5"
          filter="url(#fuzzy)"
        />
      </g>

      <!-- Eyes -->
      <g
        :transform="
          'translate(' +
          rightEyeX +
          ' ' +
          rightEyeY +
          ')'
        "
      >
        <polyline
          :points="eyeRightContour"
          fill="white"
          stroke="white"
          :stroke-width="0.0"
          stroke-linejoin="round"
          filter="url(#fuzzy)"
        />
      </g>

      <g
        :transform="
          'translate(' +
          leftEyeX +
          ' ' +
          leftEyeY +
          ')'
        "
      >
        <polyline
          :points="eyeLeftContour"
          fill="white"
          stroke="white"
          :stroke-width="0.0"
          stroke-linejoin="round"
          filter="url(#fuzzy)"
        />
      </g>

      <g
        :transform="
          'translate(' +
          rightEyeX +
          ' ' +
          rightEyeY +
          ')'
        "
      >
        <polyline
          :points="eyeRightUpper"
          fill="none"
          stroke="black"
          :stroke-width="2.5"
          stroke-linejoin="round"
          stroke-linecap="round"
          filter="url(#fuzzy)"
        />
        <polyline
          :points="eyeRightLower"
          fill="none"
          stroke="black"
          :stroke-width="2.5"
          stroke-linejoin="round"
          stroke-linecap="round"
          filter="url(#fuzzy)"
        />
        <circle
          :r="pupilSize"
          :cx="rightPupilX"
          :cy="rightPupilY"
          stroke="none"
          fill="black"
          clip-path="url(#rightEyeClipPath)"
        />
      </g>

      <g
        :transform="
          'translate(' +
          leftEyeX +
          ' ' +
          leftEyeY +
          ')'
        "
      >
        <polyline
          :points="eyeLeftUpper"
          fill="none"
          stroke="black"
          :stroke-width="2.5"
          stroke-linejoin="round"
          stroke-linecap="round"
          filter="url(#fuzzy)"
        />
        <polyline
          :points="eyeLeftLower"
          fill="none"
          stroke="black"
          :stroke-width="2.5"
          stroke-linejoin="round"
          stroke-linecap="round"
          filter="url(#fuzzy)"
        />
        <circle
          :r="pupilSize"
          :cx="leftPupilX"
          :cy="leftPupilY"
          stroke="none"
          fill="black"
          clip-path="url(#leftEyeClipPath)"
        />
      </g>

      <!-- Nose -->
      <polyline
        id="nose"
        :points="nosePoints"
        fill="black"
        stroke="black"
        :stroke-width="2.5"
        stroke-linejoin="round"
        filter="url(#fuzzy)"
      />

      <!-- Nostrils -->
      <ellipse
        :rx="2.5"
        :ry="3"
        :cx="leftNostrilX"
        :cy="leftNostrilY"
        stroke="none"
        fill="black"
      />
      <ellipse
        :rx="2.5"
        :ry="3"
        :cx="rightNostrilX"
        :cy="rightNostrilY"
        stroke="none"
        fill="black"
      />

      <!-- Tongue (draw before mouth so mouth overlaps) -->
      <polygon
        v-if="hasTongue"
        :points="tonguePoints"
        fill="rgb(255,160,180)"
        stroke="black"
        :stroke-width="2.0"
        stroke-linejoin="round"
        filter="url(#fuzzy)"
      />

      <!-- Mouth -->
      <polyline
        v-if="mouthType === 'happy' || mouthType === 'closed'"
        :points="mouthPoints"
        fill="none"
        stroke="black"
        :stroke-width="2.5"
        stroke-linejoin="round"
        stroke-linecap="round"
        filter="url(#fuzzy)"
      />
      <polyline
        v-if="mouthType === 'panting'"
        :points="mouthPoints"
        fill="rgb(215,127,140)"
        stroke="black"
        :stroke-width="2.5"
        stroke-linejoin="round"
        filter="url(#fuzzy)"
      />

      <!-- Fur lines (fluffy effect) -->
      <g id="furLines">
        <line
          v-for="(line, index) in furLines"
          :key="'fur-' + index"
          :x1="line.start[0]"
          :y1="line.start[1]"
          :x2="line.end[0]"
          :y2="line.end[1]"
          stroke="black"
          :stroke-width="1.0"
          stroke-linecap="round"
          opacity="0.6"
        />
      </g>

      <!-- Whiskers (for cats) -->
      <g id="whiskers" v-if="whiskers.length > 0">
        <line
          v-for="(whisker, index) in whiskers"
          :key="'whisker-' + index"
          :x1="whisker.start[0]"
          :y1="whisker.start[1]"
          :x2="whisker.end[0]"
          :y2="whisker.end[1]"
          stroke="black"
          :stroke-width="1.5"
          stroke-linecap="round"
          opacity="0.8"
        />
      </g>
    </svg>

    <div class="controls">
      <button @click="toggleAnimal" class="toggle-btn">
        {{ animalType === 'dog' ? '🐕 DOG' : '🐱 CAT' }}
      </button>
      <button @click="generateAnimal">ANOTHER</button>
      <button @click="downloadSVGAsPNG">DOWNLOAD</button>
    </div>
  </div>
</template>

<script>
import * as headShape from "../utils/head_shape.js";
import * as earShape from "../utils/ear_shape.js";
import * as eyeShape from "../utils/eye_shape.js";
import * as noseShape from "../utils/nose_shape.js";
import * as mouthShape from "../utils/mouth_shape.js";
import * as furPattern from "../utils/fur_pattern.js";
import * as bodyShape from "../utils/body_shape.js";
import * as furLines from "../utils/fur_lines.js";
import * as catHeadShape from "../utils/cat_head_shape.js";
import * as catEarShape from "../utils/cat_ear_shape.js";
import * as catNoseShape from "../utils/cat_nose_shape.js";
import * as catMouthShape from "../utils/cat_mouth_shape.js";
import * as catWhiskers from "../utils/cat_whiskers.js";

function randomFromInterval(min, max) {
  return Math.random() * (max - min) + min;
}

export default {
  name: "DogGenerator",
  data() {
    return {
      animalType: 'dog',  // 'dog' or 'cat'
      whiskers: [],  // For cats
      headPoints: [],
      headWidth: 0,
      headHeight: 0,
      snoutBottomY: 0,
      leftEar: [],
      rightEar: [],
      bodyPoints: [],
      legs: [],
      tail: [],
      eyeRightUpper: [],
      eyeRightLower: [],
      eyeRightContour: [],
      eyeLeftUpper: [],
      eyeLeftLower: [],
      eyeLeftContour: [],
      leftEyeX: 0,
      leftEyeY: 0,
      rightEyeX: 0,
      rightEyeY: 0,
      leftPupilX: 0,
      leftPupilY: 0,
      rightPupilX: 0,
      rightPupilY: 0,
      pupilSize: 5,
      nosePoints: [],
      leftNostrilX: 0,
      leftNostrilY: 0,
      rightNostrilX: 0,
      rightNostrilY: 0,
      mouthPoints: [],
      mouthType: 'happy',
      hasTongue: false,
      tonguePoints: [],
      spots: [],
      patches: [],
      furLines: [],
      furColor: "rgb(139,90,43)",
      spotColor: "rgb(80,50,20)",
      patchColor: "rgb(100,60,30)",
      backgroundColor: "rgb(245,245,220)",
      showSpots: true,
      showPatches: true,
      furColors: [
        "rgb(139,90,43)", // Brown
        "rgb(210,180,140)", // Tan
        "rgb(245,222,179)", // Wheat
        "rgb(160,82,45)", // Sienna
        "rgb(101,67,33)", // Dark brown
        "rgb(255,255,255)", // White
        "rgb(0,0,0)", // Black
        "rgb(192,192,192)", // Silver/Gray
        "rgb(255,215,0)", // Golden
        "rgb(244,164,96)", // Sandy
        "rgb(205,133,63)", // Peru
        "rgb(139,69,19)", // Saddle brown
        "rgb(245,245,220)", // Beige
        "rgb(222,184,135)", // Burly wood
        "rgb(188,143,143)", // Rosy brown
        "rgb(112,128,144)", // Slate gray
        "rgb(119,136,153)", // Light slate gray
        "rgb(169,169,169)", // Dark gray
        "rgb(105,105,105)", // Dim gray
        "rgb(250,240,230)", // Linen
      ],
      backgroundColors: [
        "rgb(245,245,220)", // Beige
        "rgb(176,224,230)", // Powder Blue
        "rgb(152,251,152)", // Pale Green
        "rgb(255,253,208)", // Cream
        "rgb(230,230,250)", // Lavender
        "rgb(135,206,235)", // Sky Blue
        "rgb(255,218,185)", // Peach
        "rgb(240,255,240)", // Honeydew
      ],
    };
  },
  methods: {
    toggleAnimal() {
      this.animalType = this.animalType === 'dog' ? 'cat' : 'dog';
      this.generateAnimal();
    },

    generateAnimal() {
      if (this.animalType === 'cat') {
        this.generateCat();
      } else {
        this.generateDog();
      }
    },

    generateDog() {
      // Generate head with scale variation
      let headScale = randomFromInterval(0.75, 1.35);  // Big head to small head
      let head = headShape.generateDogHeadShape();

      // Clear whiskers for dogs
      this.whiskers = [];

      // Apply scale to head
      for (let i = 0; i < head.head.length; i++) {
        head.head[i][0] *= headScale;
        head.head[i][1] *= headScale;
      }

      this.headPoints = head.head;
      this.headWidth = head.width * headScale;
      this.headHeight = head.height * headScale;
      this.snoutBottomY = head.snoutBottomY * headScale;

      // Generate ears from head connection points (scaled)
      let leftEarPoint = [head.leftEarPoint[0] * headScale, head.leftEarPoint[1] * headScale];
      let rightEarPoint = [head.rightEarPoint[0] * headScale, head.rightEarPoint[1] * headScale];

      let ears = earShape.generateBothEars(
        leftEarPoint,
        rightEarPoint,
        this.headWidth,
        this.headHeight
      );
      this.leftEar = ears.left;
      this.rightEar = ears.right;

      // Generate eyes with more variation
      let eyeWidth = randomFromInterval(this.headWidth / 10, this.headWidth / 5);
      let eyes = eyeShape.generateBothEyes(eyeWidth);
      let left = eyes.left;
      let right = eyes.right;

      this.eyeRightUpper = right.upper;
      this.eyeRightLower = right.lower;
      this.eyeRightContour = right.upper
        .slice(10, 90)
        .concat(right.lower.slice(10, 90).reverse());
      this.eyeLeftUpper = left.upper;
      this.eyeLeftLower = left.lower;
      this.eyeLeftContour = left.upper
        .slice(10, 90)
        .concat(left.lower.slice(10, 90).reverse());

      let distanceBetweenEyes = randomFromInterval(15, 35) * headScale;
      let eyeHeightOffset = (head.headCenterY - head.headHeight * randomFromInterval(0.2, 0.45)) * headScale;

      // Add more asymmetry to eye positions
      this.leftEyeX = -distanceBetweenEyes + randomFromInterval(-8, 8);
      this.leftEyeY = eyeHeightOffset + randomFromInterval(-12, 12);
      this.rightEyeX = distanceBetweenEyes + randomFromInterval(-8, 8);
      this.rightEyeY = eyeHeightOffset + randomFromInterval(-12, 12);

      // Generate pupils
      let leftInd0 = Math.floor(randomFromInterval(10, left.upper.length - 10));
      let rightInd0 = Math.floor(randomFromInterval(10, right.upper.length - 10));
      let leftInd1 = Math.floor(randomFromInterval(10, left.upper.length - 10));
      let rightInd1 = Math.floor(randomFromInterval(10, right.upper.length - 10));
      let leftLerp = randomFromInterval(0.2, 0.8);
      let rightLerp = randomFromInterval(0.2, 0.8);

      this.leftPupilY =
        left.upper[leftInd0][1] * leftLerp +
        left.lower[leftInd1][1] * (1 - leftLerp);
      this.rightPupilY =
        right.upper[rightInd0][1] * rightLerp +
        right.lower[rightInd1][1] * (1 - rightLerp);
      this.leftPupilX =
        left.upper[leftInd0][0] * leftLerp +
        left.lower[leftInd1][0] * (1 - leftLerp);
      this.rightPupilX =
        right.upper[rightInd0][0] * rightLerp +
        right.lower[rightInd1][0] * (1 - rightLerp);

      // Random pupil size with more variation
      this.pupilSize = randomFromInterval(2, 9);

      // Generate nose at the snout bottom (breed-specific)
      let nose = noseShape.generateDogNose(this.snoutBottomY, head.snoutLength, headScale);
      this.nosePoints = nose.outline;
      this.leftNostrilX = nose.leftNostril.x;
      this.leftNostrilY = nose.leftNostril.y;
      this.rightNostrilX = nose.rightNostril.x;
      this.rightNostrilY = nose.rightNostril.y;

      // Generate mouth from actual nose bottom position (already scaled)
      let mouth = mouthShape.generateMouth(nose.noseBottomY);
      this.mouthPoints = mouth.points;
      this.mouthType = mouth.type;
      this.hasTongue = mouth.hasTongue;
      if (this.hasTongue) {
        this.tonguePoints = mouth.tongue;
      }

      // Generate fur patterns (less frequent, only inside head contour)
      this.showSpots = Math.random() > 0.7;  // 30% chance
      this.showPatches = Math.random() > 0.75;  // 25% chance

      if (this.showSpots) {
        this.spots = furPattern.generateSpots(
          head.head,  // Pass head contour
          Math.floor(randomFromInterval(2, 5))  // Fewer spots
        );
      }

      if (this.showPatches) {
        this.patches = furPattern.generatePatches(
          head.head,  // Pass head contour
          Math.floor(randomFromInterval(1, 3))  // Fewer patches
        );
      }

      // Generate body (continuous posture variation)
      // Body connects directly from head bottom
      let headBottomY = head.maxY * headScale;

      let bodyData = bodyShape.generateDogBody(headBottomY, this.headWidth);

      this.bodyPoints = bodyData.body;
      this.legs = bodyData.legs;
      this.tail = bodyData.tail;

      // Generate fur lines for fluffy effect
      this.furLines = [];

      // Head fur lines (optional, 60% chance)
      if (Math.random() > 0.4) {
        let headFur = furLines.generateFurLines(head.head, 0.15);
        this.furLines = this.furLines.concat(headFur);
      }

      // Body fur lines (optional, 50% chance)
      if (Math.random() > 0.5 && this.bodyPoints.length > 0) {
        let bodyFur = furLines.generateFurLines(this.bodyPoints, 0.2);
        this.furLines = this.furLines.concat(bodyFur);
      }

      // Tail fur (wispy tip, 70% chance)
      if (Math.random() > 0.3 && this.tail.length >= 2) {
        let tailTip = this.tail[this.tail.length - 1];
        let tailPrev = this.tail[Math.max(0, this.tail.length - 3)];
        let tailDir = [tailTip[0] - tailPrev[0], tailTip[1] - tailPrev[1]];
        let tailFur = furLines.generateTailFur(tailTip, tailDir, Math.floor(randomFromInterval(3, 8)));
        this.furLines = this.furLines.concat(tailFur);
      }

      // Choose colors with more variety
      this.furColor = this.furColors[Math.floor(Math.random() * this.furColors.length)];
      this.backgroundColor = this.backgroundColors[Math.floor(Math.random() * this.backgroundColors.length)];

      // Choose spot/patch colors - ensure they're different from fur color
      let availableColors = this.furColors.filter(c => c !== this.furColor);
      this.spotColor = availableColors[Math.floor(Math.random() * availableColors.length)];
      this.patchColor = availableColors[Math.floor(Math.random() * availableColors.length)];
    },

    generateCat() {
      // Generate head with scale variation
      let headScale = randomFromInterval(0.75, 1.35);
      let head = catHeadShape.generateCatHeadShape();

      // Apply scale to head
      for (let i = 0; i < head.head.length; i++) {
        head.head[i][0] *= headScale;
        head.head[i][1] *= headScale;
      }

      this.headPoints = head.head;
      this.headWidth = head.width * headScale;
      this.headHeight = head.height * headScale;
      this.snoutBottomY = head.snoutBottomY * headScale;

      // Generate cat ears
      let leftEarPoint = [head.leftEarPoint[0] * headScale, head.leftEarPoint[1] * headScale];
      let rightEarPoint = [head.rightEarPoint[0] * headScale, head.rightEarPoint[1] * headScale];

      let ears = catEarShape.generateBothCatEars(
        leftEarPoint,
        rightEarPoint,
        this.headWidth,
        this.headHeight
      );
      this.leftEar = ears.left;
      this.rightEar = ears.right;

      // Generate eyes (cats have larger, rounder eyes)
      let eyeWidth = randomFromInterval(this.headWidth / 8, this.headWidth / 4);
      let eyes = eyeShape.generateBothEyes(eyeWidth);
      let left = eyes.left;
      let right = eyes.right;

      this.eyeRightUpper = right.upper;
      this.eyeRightLower = right.lower;
      this.eyeRightContour = right.contour;
      this.eyeLeftUpper = left.upper;
      this.eyeLeftLower = left.lower;
      this.eyeLeftContour = left.contour;

      // Position eyes (higher and wider apart for cats)
      this.leftEyeX = randomFromInterval(-this.headWidth * 0.35, -this.headWidth * 0.25);
      this.leftEyeY = head.headCenterY * headScale - this.headHeight * randomFromInterval(0.15, 0.25);
      this.rightEyeX = randomFromInterval(this.headWidth * 0.25, this.headWidth * 0.35);
      this.rightEyeY = head.headCenterY * headScale - this.headHeight * randomFromInterval(0.15, 0.25);

      // Pupil position (vertical slit for cats)
      let leftInd0 = Math.floor(randomFromInterval(0, left.upper.length - 1));
      let leftInd1 = Math.floor(randomFromInterval(0, left.lower.length - 1));
      let rightInd0 = Math.floor(randomFromInterval(0, right.upper.length - 1));
      let rightInd1 = Math.floor(randomFromInterval(0, right.lower.length - 1));

      let leftLerp = 0.5;  // Center for vertical slit
      let rightLerp = 0.5;

      this.leftPupilY =
        left.upper[leftInd0][1] * leftLerp +
        left.lower[leftInd1][1] * (1 - leftLerp);
      this.rightPupilY =
        right.upper[rightInd0][1] * rightLerp +
        right.lower[rightInd1][1] * (1 - rightLerp);
      this.leftPupilX =
        left.upper[leftInd0][0] * leftLerp +
        left.lower[leftInd1][0] * (1 - leftLerp);
      this.rightPupilX =
        right.upper[rightInd0][0] * rightLerp +
        right.lower[rightInd1][0] * (1 - rightLerp);

      this.pupilSize = randomFromInterval(3, 8);

      // Generate cat nose
      let nose = catNoseShape.generateCatNose(this.snoutBottomY, head.snoutLength, headScale);
      this.nosePoints = nose.outline;
      this.leftNostrilX = nose.leftNostril.x;
      this.leftNostrilY = nose.leftNostril.y;
      this.rightNostrilX = nose.rightNostril.x;
      this.rightNostrilY = nose.rightNostril.y;

      // Generate cat mouth
      let mouth = catMouthShape.generateCatMouth(nose.noseBottomY);
      this.mouthPoints = mouth.points;
      this.hasTongue = false;  // Cats rarely show tongue in this style

      // Generate whiskers (cat feature) - positioned on cheeks
      let whiskerY = (head.headCenterY + head.snoutBottomY) * headScale * 0.5;
      this.whiskers = catWhiskers.generateWhiskers(
        whiskerY,
        head.width * headScale * 0.4,
        headScale
      );

      // Generate fur patterns
      this.showSpots = Math.random() > 0.7;
      this.showPatches = Math.random() > 0.75;

      if (this.showSpots) {
        this.spots = furPattern.generateSpots(
          head.head,
          Math.floor(randomFromInterval(2, 5))
        );
      }

      if (this.showPatches) {
        this.patches = furPattern.generatePatches(
          head.head,
          Math.floor(randomFromInterval(1, 3))
        );
      }

      // Generate body
      let headBottomY = head.maxY * headScale;
      let bodyData = bodyShape.generateDogBody(headBottomY, this.headWidth);

      this.bodyPoints = bodyData.body;
      this.legs = bodyData.legs;
      this.tail = bodyData.tail;

      // Generate fur lines
      this.furLines = [];

      if (Math.random() > 0.4) {
        let headFur = furLines.generateFurLines(head.head, 0.15);
        this.furLines = this.furLines.concat(headFur);
      }

      if (Math.random() > 0.5 && this.bodyPoints.length > 0) {
        let bodyFur = furLines.generateFurLines(this.bodyPoints, 0.2);
        this.furLines = this.furLines.concat(bodyFur);
      }

      if (Math.random() > 0.3 && this.tail.length >= 2) {
        let tailTip = this.tail[this.tail.length - 1];
        let tailPrev = this.tail[Math.max(0, this.tail.length - 3)];
        let tailDir = [tailTip[0] - tailPrev[0], tailTip[1] - tailPrev[1]];
        let tailFur = furLines.generateTailFur(tailTip, tailDir, Math.floor(randomFromInterval(3, 8)));
        this.furLines = this.furLines.concat(tailFur);
      }

      // Choose colors
      this.furColor = this.furColors[Math.floor(Math.random() * this.furColors.length)];
      this.backgroundColor = this.backgroundColors[Math.floor(Math.random() * this.backgroundColors.length)];

      let availableColors = this.furColors.filter(c => c !== this.furColor);
      this.spotColor = availableColors[Math.floor(Math.random() * availableColors.length)];
      this.patchColor = availableColors[Math.floor(Math.random() * availableColors.length)];
    },

    downloadSVGAsPNG() {
      const svg = document.getElementById("dog-svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = document.createElement("img");
      const svgSize = svg.getBoundingClientRect();
      canvas.width = svgSize.width;
      canvas.height = svgSize.height;
      img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
      img.onload = function () {
        ctx.drawImage(img, 0, 0);
        const a = document.createElement("a");
        const e = new MouseEvent("click");
        a.download = "ugly-dog.png";
        a.href = canvas.toDataURL("image/png");
        a.dispatchEvent(e);
      };
    },
  },
  mounted() {
    this.generateAnimal();
    window.addEventListener("keydown", (e) => {
      if (e.key === " ") {
        this.generateAnimal();
      } else if (e.key === "s") {
        this.downloadSVGAsPNG();
      } else if (e.key === "t") {
        this.toggleAnimal();
      }
    });
  },
};
</script>

<style scoped>
.container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  padding: 5px;
}

svg {
  background-color: #ffffff;
}

.controls {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.toggle-btn {
  background: linear-gradient(to right, #4a90e2 50%, #e8a87c 50%);
  color: white;
  font-weight: bold;
}

.toggle-btn:hover {
  background: linear-gradient(to right, #357abd 50%, #d89564 50%);
  color: white;
}

button {
  margin-top: 10px;
  width: 200px;
  padding: 5px;
  background: transparent;
  border-width: 2px;
  font-size: 15px;
  border-color: black;
  color: black;
  font-weight: bold;
  user-select: none;
  border-radius: 10px;
  box-shadow: 2px 2px 0px 0px rgba(0, 0, 0, 0.75);
}

button:hover {
  background: black;
  color: white;
  transition: 0.3s;
}

button:active {
  background: rgb(65, 65, 65);
  box-shadow: 1px 1px 0px 0px rgba(0, 0, 0, 0.75);
}
</style>
